import { useState } from "react";
import { User } from "@/hooks/useAdminData";
import { Edit, RefreshCw, UserLock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { OrganizationMultiSelect } from "@/components/admin/OrganizationMultiSelect";

interface UsersTableProps {
	users: User[];
	isLoading: boolean;
	onEditUser: (user: User) => void;
	onRefresh: () => void;
}

const UserTableContent = ({ 
	users, 
	onEditUser
}: { 
	users: User[]; 
	onEditUser: (user: User) => void;
}) => {
	const { t } = useTranslation();
	const [resetLoading, setResetLoading] = useState<string | null>(null);

	const handleResetPassword = async (email: string) => {
		try {

			setResetLoading(email);

			const isLocalhost = () => {
				return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
			};
			

			const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
				redirectTo: isLocalhost() ? "http://localhost:8080/reset-password" : `https://${window.location.hostname}/reset-password`,
			});

			if (error) throw error;
			
			toast({
				title: t("admin.users.resetPasswordSuccess"),
				description: t("admin.users.resetPasswordSuccessDesc"),
			});
		} catch (error) {
			console.error("Error resetting password:", error);
			toast({
				title: t("admin.users.resetPasswordError"),
				description: t("admin.users.resetPasswordErrorDesc"),
				variant: "destructive",
			});
		} finally {
			setResetLoading(null);
		}
	};

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{t("common.name")}</TableHead>
					<TableHead>{t("common.email")}</TableHead>
					<TableHead>{t("companies.header")}</TableHead>
					<TableHead>{t("admin.users.role")}</TableHead>
					<TableHead>{t("admin.users.resetPassword")}</TableHead>
					<TableHead className="text-right">{t("common.actions")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{users.length === 0 ? (
					<TableRow>
						<TableCell colSpan={6} className="text-center py-4">
							{t("admin.noUsersFound")}
						</TableCell>
					</TableRow>
				) : (
					users.map((user) => (
						<TableRow key={user.id}>
							<TableCell>{user.firstName} {user.lastName}</TableCell>
							<TableCell>{user.email}</TableCell>
							<TableCell>{user.organizationName || "—"}</TableCell>
							<TableCell>
								<Badge variant={
									user.role === "super_admin" ? "destructive" :
										user.role === "admin" ? "default" : "outline"
								}>
									{user.role === "super_admin" ? t("admin.users.superAdminRole") :
										user.role === "admin" ? t("admin.users.adminRole") : t("admin.users.userRole")}
								</Badge>
							</TableCell>
							<TableCell>
								{user.role !== "super_admin" && user.active !== false ? (
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleResetPassword(user.email)}
										disabled={resetLoading === user.email}
										title={t("admin.users.resetPassword")}
										className="flex items-center gap-1"
									>
										<Send className="h-4 w-4" />
										Send Email
									</Button>
								) : (
									<span className="text-sm text-muted-foreground">
										—
									</span>
								)}
							</TableCell>
							<TableCell className="text-right flex justify-end items-center space-x-1">
								{user.role !== "super_admin" ? (
									<Button
										variant="ghost"
										size="icon"
										onClick={() => onEditUser(user)}
									>
										<Edit className="h-4 w-4" />
									</Button>
								) : (
									<span className="text-sm text-muted-foreground">
										<UserLock className="h-4 w-4 inline-block mr-1" />
									</span>
								)}
							</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</Table>
	);
};

export const UsersTable = ({
	users,
	isLoading,
	onEditUser,
	onRefresh
}: UsersTableProps) => {
	const { t } = useTranslation();
	const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);

	if (isLoading) {
		return <div className="text-center py-4">{t("admin.loading")}</div>;
	}

	// Filter users by selected organizations
	const filteredUsers = selectedOrganizations.length > 0
		? users.filter(user => user.organizationId && selectedOrganizations.includes(user.organizationId))
		: users;

	const activeUsers = filteredUsers.filter(user => user.active !== false);
	const inactiveUsers = filteredUsers.filter(user => user.active === false);

	return (
		<div className="space-y-8">
			<div className="space-y-4">
				<div className="flex justify-between items-center gap-4">
					<div className="flex-1 max-w-md">
						<OrganizationMultiSelect
							selectedOrganizations={selectedOrganizations}
							onSelectionChange={setSelectedOrganizations}
						/>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={onRefresh}
						className="flex items-center gap-1"
					>
						<RefreshCw className="h-4 w-4" />
						{t("admin.refresh")}
					</Button>
				</div>

				<div className="flex justify-between items-center">
					<p className="text-sm text-muted-foreground">
						{t("admin.users.activeList", { count: activeUsers.length })}
					</p>
				</div>

				<UserTableContent
					users={activeUsers}
					onEditUser={onEditUser}
				/>
			</div>

			{inactiveUsers.length > 0 && (
				<div className="space-y-4">

					<p className="text-sm text-muted-foreground">
						{t("admin.users.inactiveList", { count: inactiveUsers.length })}<br />
						<i>{t("admin.users.noteAboutInactive")}.</i>
					</p>
					
					<UserTableContent 
						users={inactiveUsers} 
						onEditUser={onEditUser}
					/>
				</div>
			)}
		</div>
	);
};
